import { decryptJson, encryptJson } from '@circa/shared/crypto';
import { type BodyRow, db, type RecordRow } from './db';
import { newId } from './ids';
import { keys, VaultLockedError } from './keys';
import { store } from './store';
import type { AnyEntity, BaseEntity, EntityOf, Kind } from './types';

// All writes go through here: memory first (instant UI), then encrypted to IndexedDB in a
// short batch, then marked dirty for the sync engine to push.

type Fields<K extends Kind> = Omit<EntityOf<K>, keyof BaseEntity | 'kind'> & Partial<BaseEntity>;
type Patch<K extends Kind> = Partial<Omit<EntityOf<K>, 'id' | 'kind' | 'createdAt'>>;

const pendingRecords = new Map<string, AnyEntity>();
const pendingBodies = new Map<string, { vaultId: string | null; body: unknown }>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing: Promise<void> | null = null;

/** Decrypted bodies kept around so reopening something is instant. */
const bodyCache = new Map<string, unknown>();
const BODY_CACHE_MAX = 80;

type PersistListener = () => void;
const persistListeners = new Set<PersistListener>();
/** The sync engine listens here to know there's something to push. */
export function onPersisted(l: PersistListener): () => void {
  persistListeners.add(l);
  return () => persistListeners.delete(l);
}

function scheduleFlush(delay = 250) {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, delay);
}

/** Write everything pending to IndexedDB now (before lock, sign-out, page hide). */
export async function flush(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (flushing) await flushing;
  if (!pendingRecords.size && !pendingBodies.size) return;
  const records = [...pendingRecords.values()];
  const bodies = [...pendingBodies.entries()];
  pendingRecords.clear();
  pendingBodies.clear();
  flushing = (async () => {
    const recordRows: RecordRow[] = [];
    for (const e of records) {
      try {
        recordRows.push(await toRow(e));
      } catch (err) {
        if (!(err instanceof VaultLockedError)) throw err;
        // The vault locked before we got here; keep it for when it opens again.
        pendingRecords.set(e.id, e);
      }
    }
    const bodyRows: BodyRow[] = [];
    for (const [id, b] of bodies) {
      try {
        bodyRows.push({
          id,
          dirty: 1,
          updatedAt: Date.now(),
          data: await encryptJson(keys.forVault(b.vaultId), b.body),
        });
      } catch (err) {
        if (!(err instanceof VaultLockedError)) throw err;
        pendingBodies.set(id, b);
      }
    }
    await db.transaction('rw', db.records, db.bodies, async () => {
      if (recordRows.length) await db.records.bulkPut(recordRows);
      if (bodyRows.length) await db.bodies.bulkPut(bodyRows);
    });
    for (const l of persistListeners) l();
  })();
  try {
    await flushing;
  } finally {
    flushing = null;
  }
}

async function toRow(e: AnyEntity): Promise<RecordRow> {
  return {
    id: e.id,
    kind: e.kind,
    vaultId: e.vaultId ?? '',
    updatedAt: e.updatedAt,
    deleted: e.deleted ? 1 : 0,
    // Device-only records never queue for upload (they'd crowd out real pushes).
    dirty: e.localOnly ? 0 : 1,
    localOnly: e.localOnly ? 1 : 0,
    data: await encryptJson(keys.forVault(e.vaultId), e),
  };
}

function write(e: AnyEntity) {
  store.put(e);
  pendingRecords.set(e.id, e);
  scheduleFlush();
}

// ------------------------------------------------------------------ public API

export function create<K extends Kind>(
  kind: K,
  fields: Fields<K>,
  /** updatedAt: keep an original "last edited" time (imports); only for brand-new entities */
  opts: { body?: unknown; updatedAt?: number } = {},
): EntityOf<K> {
  const now = Date.now();
  const e = {
    ...fields,
    id: fields.id ?? newId(now),
    kind,
    createdAt: fields.createdAt ?? now,
    updatedAt: opts.updatedAt ?? now,
  } as unknown as EntityOf<K>;
  write(e);
  if (opts.body !== undefined) setBody(kind, e.id, opts.body, { touch: false });
  return e;
}

export function update<K extends Kind>(
  kind: K,
  id: string,
  patch: Patch<K> | ((e: EntityOf<K>) => Patch<K>),
): EntityOf<K> | undefined {
  const cur = store.get(kind, id);
  if (!cur) return undefined;
  const p = typeof patch === 'function' ? patch(cur) : patch;
  const next = { ...cur, ...p, id, kind, updatedAt: Math.max(Date.now(), cur.updatedAt + 1) };
  if (next.vaultId !== cur.vaultId) {
    // Moving in or out of a vault re-encrypts the body with the other key too.
    void moveBody(id, cur.vaultId ?? null, next.vaultId ?? null);
  }
  write(next as AnyEntity);
  return next as EntityOf<K>;
}

/** Soft delete: goes to Trash, syncs as deleted. */
export function remove(kind: Kind, id: string) {
  update(kind, id, { deleted: true } as Patch<Kind>);
}

export function restore(kind: Kind, id: string) {
  update(kind, id, { deleted: false } as Patch<Kind>);
}

/** Delete forever: the tombstone stays (so other devices delete it too) but content is wiped. */
export function destroy(kind: Kind, id: string) {
  const cur = store.get(kind, id);
  if (!cur) return;
  const tomb = {
    id,
    kind,
    createdAt: cur.createdAt,
    updatedAt: Math.max(Date.now(), cur.updatedAt + 1),
    deleted: true,
    vaultId: cur.vaultId ?? null,
    localOnly: cur.localOnly,
  } as AnyEntity;
  write(tomb);
  bodyCache.delete(id);
  pendingBodies.delete(id);
  void db.bodies.delete(id);
}

/** Many changes at once with one re-render. */
export function batch(fn: () => void) {
  store.batch(fn);
}

export function get<K extends Kind>(
  kind: K,
  id: string | null | undefined,
): EntityOf<K> | undefined {
  return store.get(kind, id);
}

export function list<K extends Kind>(kind: K): EntityOf<K>[] {
  return store.list(kind);
}

// ------------------------------------------------------------------ bodies

export async function getBody<T = unknown>(kind: Kind, id: string): Promise<T | undefined> {
  if (pendingBodies.has(id)) return pendingBodies.get(id)!.body as T;
  if (bodyCache.has(id)) {
    const v = bodyCache.get(id);
    // refresh LRU position
    bodyCache.delete(id);
    bodyCache.set(id, v);
    return v as T;
  }
  const row = await db.bodies.get(id);
  if (!row) return undefined;
  const e = store.get(kind, id);
  const body = await decryptJson<T>(keys.forVault(e?.vaultId), row.data);
  cacheBody(id, body);
  return body;
}

/** Saves a body (debounced to disk). `touch` bumps the entity so sync sends it. */
export function setBody(
  kind: Kind,
  id: string,
  body: unknown,
  opts: { touch?: boolean; patch?: Record<string, unknown> } = {},
) {
  const e = store.get(kind, id);
  cacheBody(id, body);
  pendingBodies.set(id, { vaultId: e?.vaultId ?? null, body });
  if (e && (opts.touch !== false || opts.patch)) {
    update(kind, id, (opts.patch ?? {}) as Patch<Kind>);
  } else {
    scheduleFlush(400);
  }
}

function cacheBody(id: string, body: unknown) {
  bodyCache.delete(id);
  bodyCache.set(id, body);
  if (bodyCache.size > BODY_CACHE_MAX) {
    const first = bodyCache.keys().next().value;
    if (first !== undefined) bodyCache.delete(first);
  }
}

export function invalidateBody(id: string) {
  bodyCache.delete(id);
}

async function moveBody(id: string, fromVault: string | null, toVault: string | null) {
  const row = await db.bodies.get(id);
  if (!row || pendingBodies.has(id)) {
    const p = pendingBodies.get(id);
    if (p) p.vaultId = toVault;
    return;
  }
  const body = await decryptJson(keys.forVault(fromVault), row.data);
  // A newer save may have landed while decrypting: keep it (it already has the new vault).
  const newer = pendingBodies.get(id);
  if (newer) {
    newer.vaultId = toVault;
    return;
  }
  pendingBodies.set(id, { vaultId: toVault, body });
  scheduleFlush();
}

// ------------------------------------------------------------------ loading

/** Kinds the first screens need; the rest load right after without blocking. */
const FIRST: Kind[] = ['setting', 'vault', 'sleep', 'day', 'measurement', 'food', 'foodItem'];

/**
 * Decrypts everything on disk into memory. Resolves once the first-screen kinds are in;
 * the rest keep loading (store.isLoaded / useKindLoaded tell when a kind is complete).
 */
export async function loadAll(): Promise<void> {
  const rows = await db.records.toArray();
  const first = rows.filter((r) => FIRST.includes(r.kind as Kind));
  const rest = rows.filter((r) => !FIRST.includes(r.kind as Kind));
  await decryptInto(first);
  store.markLoaded(FIRST);
  void (async () => {
    await decryptInto(rest);
    store.markLoaded(
      (
        [
          'file',
          'template',
          'calendar',
          'event',
          'journal',
          'habit',
          'habitLog',
          'goal',
          'chat',
          'message',
          'persona',
          'aiProject',
          'story',
          'chapter',
          'bible',
          'space',
          'studioItem',
          'studioRef',
          'voice',
          'person',
        ] as Kind[]
      ).filter((k) => !FIRST.includes(k)),
    );
  })();
}

/** Records in a vault that just opened. */
export async function loadVault(vaultId: string): Promise<void> {
  const rows = await db.records.where('vaultId').equals(vaultId).toArray();
  await decryptInto(rows);
}

export function unloadVault(vaultId: string) {
  store.evictWhere((e) => e.vaultId === vaultId);
  for (const id of [...bodyCache.keys()]) {
    const e = store.find(id);
    if (!e) bodyCache.delete(id);
  }
}

async function decryptInto(rows: RecordRow[]) {
  const CHUNK = 250;
  for (let i = 0; i < rows.length; i += CHUNK) {
    const chunk = rows.slice(i, i + CHUNK);
    const out = await Promise.all(
      chunk.map(async (r) => {
        if (r.vaultId && !keys.hasVault(r.vaultId)) return null;
        try {
          const e = await decryptJson<AnyEntity>(keys.forVault(r.vaultId || null), r.data);
          // The envelope's flag wins: a server tombstone may carry the old (undeleted) data.
          if (r.deleted && !e.deleted) e.deleted = true;
          return e;
        } catch {
          return null;
        }
      }),
    );
    store.putMany(out.filter((e): e is AnyEntity => e !== null));
    // Let the UI breathe between chunks.
    if (i + CHUNK < rows.length) await new Promise((r) => setTimeout(r, 0));
  }
}

/** Count of records per locked vault (shown on the vault's lock screen). */
export async function lockedCounts(): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  await db.records.each((r) => {
    if (r.vaultId && !keys.hasVault(r.vaultId) && !r.deleted) {
      out.set(r.vaultId, (out.get(r.vaultId) ?? 0) + 1);
    }
  });
  return out;
}

/** Forget everything in memory (lock / sign out). */
export function clearMemory() {
  pendingRecords.clear();
  pendingBodies.clear();
  bodyCache.clear();
  store.clear();
}

/**
 * Apply a record that came from the server (already written to disk by the sync engine).
 * Returns false when it can't be opened yet (vault locked).
 */
export async function applyRemote(row: RecordRow): Promise<boolean> {
  if (row.vaultId && !keys.hasVault(row.vaultId)) return false;
  const e = await decryptJson<AnyEntity>(keys.forVault(row.vaultId || null), row.data);
  if (row.deleted && !e.deleted) e.deleted = true;
  // A newer local edit still waiting to be written wins.
  const pending = pendingRecords.get(e.id);
  if (pending && pending.updatedAt > e.updatedAt) return true;
  bodyCache.delete(e.id);
  store.put(e);
  return true;
}

export function hasPendingWrite(id: string): boolean {
  return pendingRecords.has(id) || pendingBodies.has(id);
}
